import type { TemplateMeta } from "@/parser/templateSerializer";

const CATEGORY_OPTIONS = ["", "eval", "note", "measure"] as const;

interface EditorHeaderProps {
  mode: "build" | "fill";
  displayName: string;
  meta: TemplateMeta;
  saveStatus: string;
  statusLabel: string;
  storagePrefix: string;
  showSource: boolean;
  setShowSource: React.Dispatch<React.SetStateAction<boolean>>;
  showReport: boolean;
  setShowReport: React.Dispatch<React.SetStateAction<boolean>>;
  templateId?: string;
  isSeed: boolean;
  onNameChange: (name: string) => void;
  onCategoryChange: (category: string) => void;
  onCopyReport: () => void;
  onMarkCompleted: () => void;
  onSaveAs: () => void;
  onDeleteClick: () => void;
  menuOpen: boolean;
  setMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onNavigateBack: () => void;
}

function toggleWithStorage(
  setter: React.Dispatch<React.SetStateAction<boolean>>,
  storageKey: string,
) {
  setter((p: boolean) => {
    const next = !p;
    localStorage.setItem(storageKey, JSON.stringify(next));
    return next;
  });
}

function PanelToggleButtons({
  setShowReport,
  setShowSource,
  showReport,
  showSource,
  storagePrefix,
}: Readonly<
  Pick<
    EditorHeaderProps,
    "showSource" | "setShowSource" | "showReport" | "setShowReport" | "storagePrefix"
  >
>) {
  return (
    <>
      <button
        className={showSource ? "active" : ""}
        onClick={() => {
          toggleWithStorage(setShowSource, `${storagePrefix}-show-source`);
        }}
      >
        {showSource ? "Hide Source" : "Source"}
      </button>
      <button
        className={showReport ? "active" : ""}
        onClick={() => {
          toggleWithStorage(setShowReport, `${storagePrefix}-show-report`);
        }}
      >
        {showReport ? "Hide Report" : "Show Report"}
      </button>
    </>
  );
}

type HeaderActionProps = Readonly<
  Omit<
    EditorHeaderProps,
    | "displayName"
    | "meta"
    | "saveStatus"
    | "statusLabel"
    | "templateId"
    | "onNameChange"
    | "onCategoryChange"
    | "onNavigateBack"
  >
>;

function ModeSpecificActions({
  isSeed,
  menuOpen,
  mode,
  onCopyReport,
  onDeleteClick,
  onMarkCompleted,
  onSaveAs,
  setMenuOpen,
}: HeaderActionProps) {
  return (
    <>
      {mode === "fill" && <button onClick={onCopyReport}>Copy Report</button>}
      {mode === "fill" && (
        <button className="primary" onClick={onMarkCompleted}>
          Mark Completed
        </button>
      )}
      {mode === "build" && isSeed && (
        <button className="primary" onClick={onSaveAs}>
          Save As
        </button>
      )}
      {mode === "build" && !isSeed && (
        <button className="danger" onClick={onDeleteClick}>
          Delete
        </button>
      )}
      {mode === "fill" && (
        <MoreMenu menuOpen={menuOpen} setMenuOpen={setMenuOpen} onDeleteClick={onDeleteClick} />
      )}
    </>
  );
}

function HeaderActions(props: HeaderActionProps) {
  return (
    <div className="filler-header-actions">
      <PanelToggleButtons
        showSource={props.showSource}
        setShowSource={props.setShowSource}
        showReport={props.showReport}
        setShowReport={props.setShowReport}
        storagePrefix={props.storagePrefix}
      />
      <ModeSpecificActions {...props} />
    </div>
  );
}

function MoreMenu({
  menuOpen,
  onDeleteClick,
  setMenuOpen,
}: Readonly<{
  menuOpen: boolean;
  setMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onDeleteClick: () => void;
}>) {
  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => {
          setMenuOpen((p) => !p);
        }}
      >
        ⋮ More
      </button>
      {menuOpen && (
        <div className="inline-menu-dropdown">
          <button
            className="inline-menu-item danger"
            onClick={() => {
              setMenuOpen(false);
              onDeleteClick();
            }}
          >
            Delete Evaluation
          </button>
        </div>
      )}
    </div>
  );
}

function CategorySelect({
  meta,
  onCategoryChange,
}: Readonly<Pick<EditorHeaderProps, "meta" | "onCategoryChange">>) {
  return (
    <select
      className="category-select"
      value={meta.category ?? ""}
      onChange={(e) => {
        onCategoryChange(e.target.value);
      }}
      title="Category"
    >
      {CATEGORY_OPTIONS.map((c) => (
        <option key={c} value={c}>
          {c || "(no category)"}
        </option>
      ))}
    </select>
  );
}

export function EditorHeader({
  displayName,
  isSeed,
  meta,
  mode,
  onCategoryChange,
  onNameChange,
  onNavigateBack,
  saveStatus,
  statusLabel,
  ...actionProps
}: Readonly<EditorHeaderProps>) {
  return (
    <>
      <div className="filler-header">
        <button className="filler-back" onClick={onNavigateBack}>
          &larr;
        </button>
        <input
          type="text"
          className="filler-name-input"
          value={displayName}
          onChange={(e) => {
            onNameChange(e.target.value);
          }}
          placeholder={mode === "build" ? "Template name" : undefined}
        />
        {mode === "build" && <CategorySelect meta={meta} onCategoryChange={onCategoryChange} />}
        {!isSeed && <span className={`save-status ${saveStatus}`}>{statusLabel}</span>}
        <HeaderActions mode={mode} isSeed={isSeed} {...actionProps} />
      </div>
      {isSeed && mode === "build" && (
        <div className="seed-banner">
          Built-in template — changes aren&apos;t saved. Save As to keep your edits.
        </div>
      )}
    </>
  );
}
